import { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ExternalLink, Image as ImageIcon, Star } from "lucide-react";
import {
  getFavoriteChipStyle,
  getSourceDisplay,
  isFavoriteTag,
  FAVORITE_LABEL,
  type ReadwiseLibraryMatch,
  type ReadwiseHighlightRow,
} from "@/lib/readwiseSource";
import { safeHref } from "@/lib/safeHref";
import { deriveLibraryTitle } from "@/lib/library";
import { getSourceRowToneVars, readwiseTokenToLibraryType } from "@/lib/sourceTypeRowTone";
import { cn } from "@/lib/utils";

type Props = {
  r: ReadwiseHighlightRow;
  libraryMatch?: ReadwiseLibraryMatch | null;
  returnTo?: string;
  expanded: boolean;
  onToggle: () => void;
};

export const HighlightRow = forwardRef<HTMLButtonElement, Props>(({ r, libraryMatch = null, returnTo = "readwise-highlights", expanded, onToggle }, ref) => {
  const src = getSourceDisplay(r.book_source);
  const SrcIcon = src.Icon;
  const tone = getSourceRowToneVars(readwiseTokenToLibraryType(src.tokenKey));
  const hasFavorite = r.readwise_tags.some(isFavoriteTag);
  const navigate = useNavigate();
  const handleMakeImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    sessionStorage.setItem(
      "excerptus.imageStudioPrefill",
      JSON.stringify({
        bodyHtml: DOMPurify.sanitize(r.highlight_text),
        author: r.book_author ?? "",
        publication: r.book_title ?? "",
      }),
    );
    navigate("/image-studio");
  };

  return (
    <Card
      className="shadow-soft overflow-hidden group"
      style={{ backgroundColor: tone.idle, borderLeft: `4px solid ${tone.accent}` }}
    >
      <div className="flex items-stretch">
        <button
          ref={ref}
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse highlight" : "Expand highlight"}
          className="flex-1 min-w-0 text-left flex items-center gap-2 px-3 py-2 min-h-12 hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate text-muted-foreground">
              {r.highlight_text}
            </p>
            <div className="mt-1 flex items-center gap-2 min-w-0">
              {r.book_title && (
                <span
                  className="font-normal text-xs truncate min-w-0"
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  {r.book_title}
                </span>
              )}
              {r.book_author && (
                <span className="text-xs text-muted-foreground truncate min-w-0">
                  · {r.book_author}
                </span>
              )}
              <Badge
                variant="outline"
                className="text-xs gap-1 border h-5 px-1.5 shrink-0"
                style={{ backgroundColor: "transparent", color: tone.title, borderColor: tone.selected, borderWidth: 1 }}
              >
                <SrcIcon className="h-3 w-3" />
                {src.label}
              </Badge>
              {hasFavorite && (
                <Star
                  className="h-3.5 w-3.5 shrink-0"
                  style={{ color: `hsl(var(--chip-favorite-fg))` }}
                  aria-label="Favorite"
                />
              )}
            </div>
          </div>
          <span
            className="h-8 w-8 shrink-0 inline-flex items-center justify-center text-muted-foreground"
            aria-hidden="true"
          >
            <ChevronDown
              className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")}
            />
          </span>
        </button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleMakeImage}
          title="Send to Image Studio"
          aria-label="Send to Image Studio"
          className="shrink-0 self-center mr-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hidden md:inline-flex"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
      </div>

      {expanded && (
        <div className="bg-muted/30 border-t px-4 py-3 space-y-3">
          <p className="text-base leading-relaxed whitespace-pre-wrap text-muted-foreground">{r.highlight_text}</p>
          {r.note && (
            <p className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-3">
              Note: {r.note}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {r.location &&
              r.location_type?.toLowerCase() !== "order" &&
              !(!r.location_type && /^\d+$/.test(r.location)) && (
                <span>
                  {r.location_type ?? "loc"} {r.location}
                </span>
              )}
            {r.highlighted_at && (
              <span>{r.location ? "· " : ""}{new Date(r.highlighted_at).toLocaleDateString()}</span>
            )}
            {(() => { const safe = safeHref(r.readwise_url); return safe ? (
              <a
                href={safe}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                View in Readwise <ExternalLink className="h-3 w-3" />
              </a>
            ) : null; })()}
          </div>
          <div className="flex flex-wrap gap-1">
            {r.readwise_tags.map((t) =>
              isFavoriteTag(t) ? (
                <Badge
                  key={t}
                  variant="outline"
                  className="text-xs gap-1 border"
                  style={getFavoriteChipStyle()}
                >
                  <Star className="h-3 w-3" />
                  {FAVORITE_LABEL}
                </Badge>
              ) : (
                <Badge key={t} variant="secondary" className="text-xs">
                  {t}
                </Badge>
              ),
            )}
          </div>
          {libraryMatch ? (
            <div className="rounded-md border border-border bg-background p-3 text-sm">
              <p className="font-medium text-foreground">Matching source record found</p>
              <p className="text-muted-foreground">{deriveLibraryTitle(libraryMatch)}{libraryMatch.author_text ? ` — ${libraryMatch.author_text}` : ""}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <a href={`/my-library?item=${libraryMatch.id}&returnTo=${encodeURIComponent(returnTo)}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">Open source</a>
                <a href={`/new-excerpt?libraryItem=${libraryMatch.id}&returnTo=${encodeURIComponent(returnTo)}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">Create excerpt</a>
                <a href={`/my-library?returnTo=${encodeURIComponent(returnTo)}`} onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">Create new source</a>
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={handleMakeImage}>
              <ImageIcon className="h-4 w-4" /> Make image
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
});
HighlightRow.displayName = "HighlightRow";
